import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { buildFraudQueueView, type FraudQueueView } from './fraudQueueModel';
import type {
  CashoutRequest,
  Market,
  Transaction,
  User,
} from '../dataProvider/types';

/**
 * The Fraud queue view, read through the aggregation seam (`buildFraudQueueView`),
 * NOT computed in the component. For v1 it pulls raw users, transactions and
 * cashout requests from the mock provider and aggregates client-side; the real-data
 * migration replaces the fetch-and-aggregate here with a single server-side
 * endpoint and the surface does not change. Same seam idiom as useSubscriptions.
 *
 * INVESTIGATIVE-GLOBAL: the queue does NOT read the region switcher. It is global by
 * default; the optional `regionFilter` (the surface's own facet) narrows by the
 * user's market by exact value, never through marketsForScope.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useFraudQueue = (
  regionFilter: Market | null,
): {
  view: FraudQueueView | null;
  isLoading: boolean;
} => {
  const dataProvider = useDataProvider();

  const { data, isLoading } = useQuery({
    queryKey: ['fraud-queue', regionFilter],
    queryFn: async () => {
      const [users, transactions, cashoutRequests] = await Promise.all([
        dataProvider.getList('users', ALL),
        dataProvider.getList('transactions', ALL),
        dataProvider.getList('cashoutRequests', ALL),
      ]);
      return buildFraudQueueView(
        {
          users: users.data as unknown as User[],
          transactions: transactions.data as unknown as Transaction[],
          cashoutRequests: cashoutRequests.data as unknown as CashoutRequest[],
        },
        { regionFilter },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
